import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const STEPS = [
  { key: "focus", q: "What would you most like support with?", options: ["Energy","Hydration","Recovery","Immune support","Performance","Healthy aging","General wellness","I'm not sure"] },
  { key: "frequency", q: "How often do you feel run down or low energy?", options: ["Rarely","A few times a month","Weekly","Most days"] },
  { key: "routine", q: "Do you currently take supplements?", options: ["No","Occasionally","A daily multi","A curated stack"] },
  { key: "goal", q: "What best describes your goal?", options: ["Feel better day-to-day","Optimize performance","Age well","Address a specific concern"] },
];

const RESULTS = {
  iv: { title: "Explore IV Therapy", copy: "Fast-acting hydration and nutrient support could be a great match.", ctas: [["Book an IV","/login"],["Learn about IV","/iv-therapy"]] },
  consultation: { title: "A consultation may be the best place to start.", copy: "Sit down with Steve or Lilly to design a plan around your goals.", ctas: [["Book a Consult","/book/consult"],["Meet the team","/about"]] },
  supplements: { title: "Browse Restore Wellness supplements", copy: "Practitioner-grade formulas selected for real results.", ctas: [["Shop Wellness","/shop"],["Book a Consult","/book/consult"]] },
};

export default function Quiz() {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const pct = ((i) / STEPS.length) * 100;

  const answer = async (val) => {
    const next = { ...answers, [STEPS[i].key]: val };
    setAnswers(next);
    if (i + 1 < STEPS.length) return setI(i + 1);
    try {
      const { data } = await api.post("/quiz/submit", { answers: next });
      setResult(data.recommendation);
    } catch { setResult({ primary: "consultation" }); }
  };

  if (result) {
    const r = RESULTS[result.primary] || RESULTS.consultation;
    return (
      <div className="section-y" data-testid="quiz-result">
        <div className="container-x max-w-2xl text-center">
          <div className="overline">Your recommendation</div>
          <h1 className="font-display text-4xl lg:text-5xl text-sage-800 mt-3">{r.title}</h1>
          <p className="mt-5 text-sage-800/70">{r.copy}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {r.ctas.map(([label, to]) => (
              <Button key={to} asChild className="btn-pill h-11 px-6 bg-sage-700 hover:bg-sage-800 text-white" data-testid={`result-cta-${to.replace(/\W+/g,'')}`}>
                <Link to={to}>{label}</Link>
              </Button>
            ))}
          </div>
          <p className="mt-8 text-xs text-sage-800/50">Quiz results are informational and not medical advice.</p>
        </div>
      </div>
    );
  }

  const s = STEPS[i];
  return (
    <div className="section-y" data-testid="quiz-page">
      <div className="container-x max-w-2xl">
        <Progress value={pct} className="h-1 bg-sand-200" data-testid="quiz-progress" />
        <div className="mt-3 text-xs text-sage-800/50 uppercase tracking-widest">Question {i+1} of {STEPS.length}</div>
        <AnimatePresence mode="wait">
          <motion.div key={s.key} initial={{opacity:0, y:16}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-16}} transition={{duration:0.35}}>
            <h1 className="font-display text-3xl lg:text-4xl text-sage-800 mt-6">{s.q}</h1>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {s.options.map((o) => (
                <button key={o} data-testid={`quiz-opt-${o.replace(/\s+/g,'-').toLowerCase()}`} onClick={()=>answer(o)} className="card-lift text-left rounded-2xl bg-white border border-sand-200/60 px-5 py-4 hover:border-sage-700/40 text-sage-800">{o}</button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
